import { useEffect, useState } from 'react'
import * as BABYLON from '@babylonjs/core'
import { StudioTool, WallSide } from '../types'
import { WALL_MESH_NAME } from '../constants'

export interface UseStudioToolsCallbacks {
  onSelectArt: (id: string | null) => void
  onDoubleClickArt: (id: string) => void
  onHangWall: (side: WallSide, point: BABYLON.Vector3) => void
  onPinPoint: (point: BABYLON.Vector3, normal: BABYLON.Vector3) => void
}

export interface UseStudioToolsApi {
  tool: StudioTool
  setTool: (tool: StudioTool) => void
}

const WALL_SIDE_BY_MESH: Record<string, WallSide> = {
  [WALL_MESH_NAME[-1]]: -1,
  [WALL_MESH_NAME[1]]: 1,
  [WALL_MESH_NAME[0]]: 0,
}

/** Pointer-picking + tool state — select/hang/comment, mirroring the prototype's click-vs-drag gate. */
export function useStudioTools(
  scene: BABYLON.Scene | null,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  callbacks: UseStudioToolsCallbacks,
): UseStudioToolsApi {
  const [tool, setTool] = useState<StudioTool>('select')

  useEffect(() => {
    const canvas = canvasRef.current
    canvas?.classList.toggle('studio-tool-hang', tool === 'hang')
    canvas?.classList.toggle('studio-tool-comment', tool === 'comment')
  }, [tool, canvasRef])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!scene || !canvas) return

    let pointerDownAt: { x: number; y: number } | null = null
    const onPointerDown = (e: PointerEvent) => {
      pointerDownAt = { x: e.clientX, y: e.clientY }
    }
    const onPointerUp = (e: PointerEvent) => {
      const start = pointerDownAt
      pointerDownAt = null
      if (!start || Math.hypot(e.clientX - start.x, e.clientY - start.y) > 6) return

      const pick = scene.pick(scene.pointerX, scene.pointerY)
      if (!pick.hit || !pick.pickedMesh) {
        if (tool === 'select') callbacks.onSelectArt(null)
        return
      }
      const mesh = pick.pickedMesh

      if (tool === 'comment' && pick.pickedPoint) {
        const normal = pick.getNormal(true, true) || new BABYLON.Vector3(0, 0, 1)
        callbacks.onPinPoint(pick.pickedPoint, normal)
        return
      }

      if (tool === 'hang' && mesh.name in WALL_SIDE_BY_MESH && pick.pickedPoint) {
        callbacks.onHangWall(WALL_SIDE_BY_MESH[mesh.name], pick.pickedPoint)
        return
      }

      if (tool === 'select') {
        const artId = mesh.metadata?.studioArtId as string | undefined
        callbacks.onSelectArt(artId || null)
      }
    }
    const onDblClick = () => {
      const pick = scene.pick(scene.pointerX, scene.pointerY)
      const artId = pick.hit ? (pick.pickedMesh?.metadata?.studioArtId as string | undefined) : undefined
      if (artId) callbacks.onDoubleClickArt(artId)
    }

    canvas.addEventListener('pointerdown', onPointerDown)
    canvas.addEventListener('pointerup', onPointerUp)
    canvas.addEventListener('dblclick', onDblClick)
    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown)
      canvas.removeEventListener('pointerup', onPointerUp)
      canvas.removeEventListener('dblclick', onDblClick)
    }
    // callbacks is a fresh object each render by design (closures over latest state) —
    // re-binding listeners each render is cheap and keeps them from going stale.
  }, [scene, canvasRef, tool, callbacks])

  return { tool, setTool }
}
